import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  Signal,
  signal
} from '@angular/core';
import { FormControlDirective, FormControlName, FormGroupDirective, FormGroupName, NgForm, NgModel } from '@angular/forms';
import { Subject } from 'rxjs';

@Component({
  selector: 'mbs-wizard-step',
  template: '@if (visible()) { <ng-content />}',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.-active]': 'activated'
  }
})
export class WizardStepComponent implements OnDestroy {
  private cd = inject(ChangeDetectorRef);

  @Input() title = 'Some Step';
  @Input() changes: boolean;
  @Input() blockEnterIfControlFocus = true;
  @Input() documentationLink: string;
  @Input() e2eLocator: string;
  @Input()
  public set valid(v: boolean) {
    this.myValid = v;
    this.change.next();
  }
  public get valid(): boolean {
    return this.myValid;
  }
  public get visible(): Signal<boolean> {
    return this.myVisible;
  }
  public set visible(v: boolean) {
    if (v && !this.myVisible() && this.ngModel?.value && !this.ignoreVisibleAction) {
      this.ngModel.control.markAsUntouched();
      this.ngModel.control.markAsPristine();
    }
    if (!v && this.myVisible() && this.ngModel?.touched) this.ignoreVisibleAction = true;
    if (v && !this.visited) this.visited = true;

    this.myVisible.set(v);
  }
  public get hasChanges(): boolean {
    if (this.changes != null) return this.changes;

    return (
      (this.visited && this.ngModel && this.ngModel.dirty && this.ngModel?.touched) ||
      (this.ngFormControlName && this.ngFormControlName.dirty) ||
      (this.ngFormControlDirective && this.ngFormControlDirective.dirty) ||
      (this.ngFormGroupDirective && this.ngFormGroupDirective.dirty) ||
      (this.ngFormGroupName && this.ngFormGroupName.dirty) ||
      (this.ngForm && this.ngForm.dirty) ||
      false
    );
  }

  public visited = false;
  public completed = false;
  public activated = false;
  public disabled = false;
  public canOpen = false;
  public change = new Subject<void>();
  public changesBeforeValidation = false;
  public isNext = true;
  public isSave = false;

  private ignoreVisibleAction = false;
  private myVisible = signal(false);
  private myValid = false;

  @Output('next') nextEvent = new EventEmitter<WizardStepComponent>();
  @Output('nextButtonClicked') nextButtonClicked = new EventEmitter();
  @Output('backButtonClicked') backButtonClicked = new EventEmitter();
  @Output('saveButtonClicked') saveButtonClicked = new EventEmitter();

  @ContentChild(NgModel, { static: false }) ngModel: NgModel;
  @ContentChild(FormControlName, { static: false }) ngFormControlName: FormControlName;
  @ContentChild(FormControlDirective, { static: false }) ngFormControlDirective: FormControlDirective;
  @ContentChild(FormGroupDirective, { static: false }) ngFormGroupDirective: FormGroupDirective;
  @ContentChild(FormGroupName, { static: false }) ngFormGroupName: FormGroupName;
  @ContentChild(NgForm, { static: false }) ngForm: NgForm;

  ngOnDestroy(): void {
    this.cd.detach();
    this.change.unsubscribe();
  }

  activate(): void {
    this.activated = true;
    this.completed = false;
    this.canOpen = true;
    this.visible = true;
    this.disabled = false;
  }

  notActivate(): void {
    this.completed = this.valid;
    this.activated = false;
    this.visible = false;
  }

  complete(): void {
    this.activated = false;
    this.completed = this.valid;
    this.canOpen = true;
    this.visible = false;
    this.disabled = false;
  }

  reset(): void {
    this.disabled = true;
    this.canOpen = false;
    this.visible = false;
    this.activated = false;
    this.completed = false;
  }
}
